import { useState } from "react";
import type { MainTemplate, RelaySite, TemplateRelaySite } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { InheritedModelConfigEditor } from "@/components/InheritedModelConfigEditor";

const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function RelayBindingEditModal({
  binding,
  site,
  template,
  saving,
  error,
  onSave,
  onClose,
}: {
  binding: TemplateRelaySite;
  site: RelaySite | undefined;
  template: MainTemplate | undefined;
  saving?: boolean;
  error?: string | null;
  onSave: (body: {
    groups: string;
    priority: number;
    proxy: string;
    models: string | null;
    model_mapping: string | null;
  }) => void;
  onClose: () => void;
}) {
  const [groups, setGroups] = useState(binding.groups ?? "");
  const [priority, setPriority] = useState(String(binding.priority));
  const [proxy, setProxy] = useState(binding.proxy ?? "");
  const [models, setModels] = useState<string | null>(binding.models);
  const [modelMapping, setModelMapping] = useState<string | null>(binding.model_mapping);

  const nextPriority = Number(priority);
  const priorityValid =
    priority.trim() !== "" && Number.isInteger(nextPriority) && nextPriority >= 0;
  const dirty =
    groups !== (binding.groups ?? "") ||
    nextPriority !== binding.priority ||
    proxy !== (binding.proxy ?? "") ||
    models !== binding.models ||
    modelMapping !== binding.model_mapping;
  const canSave = dirty && priorityValid && !saving;

  return (
    <Modal
      title={`编辑绑定 · ${binding.site_name || `#${binding.site_id}`} × ${binding.template_name || template?.name || `#${binding.template_id}`}`}
      subtitle="模板与站点是绑定的联合主键，不可在此修改；如需换绑请先解绑再新增。"
      onClose={onClose}
      defaultSize="lg"
      sizeControls
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) return;
          onSave({
            groups: groups.trim(),
            priority: nextPriority,
            proxy: proxy.trim(),
            models,
            model_mapping: modelMapping,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>分组 groups</Label>
            <Input
              value={groups}
              onChange={(e) => setGroups(e.target.value)}
              placeholder="default,premium"
            />
          </div>
          <div className="space-y-1">
            <Label>优先级</Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">MAX 选路，必须 ≥ 0</p>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>代理地址</Label>
            <Input
              className={inpCls}
              value={proxy}
              onChange={(e) => setProxy(e.target.value)}
              placeholder="socks5://192.168.77.11:10808"
              spellCheck={false}
            />
            <p className="text-[11px] text-muted-foreground">
              可选出站代理，空 = 不设；分发时写入 new-api 渠道 setting.proxy
            </p>
          </div>
        </div>

        <div className="rounded-lg border bg-muted/20 p-3">
          <InheritedModelConfigEditor
            models={models}
            modelMapping={modelMapping}
            inheritedModels={(site?.models ?? template?.models) || ""}
            inheritedModelMapping={(site?.model_mapping ?? template?.model_mapping) || ""}
            onModelsChange={setModels}
            onModelMappingChange={setModelMapping}
          />
        </div>

        {!priorityValid && priority.trim() !== "" && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            优先级必须为 ≥ 0 的整数
          </div>
        )}
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={!canSave}>
            {saving ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
